import type { FC, FocusEvent } from 'react';

import type { FormFieldInputProps, FormInstance } from '@zen/Components/Form/types';
import type { NumberInputProps } from '@zen/DesignSystem';
import { NumberInput } from '@zen/DesignSystem';
import type { Nullable } from '@zen/utils/typescript';

import type { FormFieldProps } from '../FormField';
import FormField from '../FormField';

type InputStepType = number | string;

interface Props
  extends
    FormFieldProps,
    Pick<NumberInputProps, 'hideSpinButtons' | 'maxDecimalDigits' | 'maxWholeDigits' | 'showStepperButtons' | 'size'> {
  disabled?: boolean;
  hasSuffix?: boolean;
  max?: number;
  min?: number;
  onBlur?: (event: FocusEvent<HTMLInputElement>, name: string, form: FormInstance<unknown>) => void;
  onChange?: (value: number, name: string, form: FormInstance<unknown>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>, name: string, form: FormInstance<unknown>) => void;
  step?: InputStepType;
}

const FormNumberInput: FC<Props> = (props: Props) => {
  const { onBlur, onChange, onFocus, step = 0.01, ...rest } = props;

  return (
    <FormField {...rest}>
      {(field: FormFieldInputProps<string>, form: FormInstance<unknown>) => {
        const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
          field.onBlur(e);

          if (onBlur) {
            onBlur(e, field.name, form);
          }
        };

        const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
          if (onFocus) {
            onFocus(e, field.name, form);
          }
        };

        const handleChange = (newValue: Nullable<number>) => {
          form.setFieldValue(field.name, newValue);

          if (onChange && newValue) {
            onChange(newValue, field.name, form);
          }
        };

        return (
          <NumberInput {...field} id={field.name} onBlur={handleBlur} onChange={handleChange} onFocus={handleFocus} step={step} />
        );
      }}
    </FormField>
  );
};

export type { Props as FormNumberInputProps };
export default FormNumberInput;
